/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import {
  LAST_12_WEEKS_FROM_NOW_RANGE_PRESET_LOCAL_FULL,
  LAST_30_DAYS_RANGE_PRESET_LOCAL_FULL,
  LAST_6_MONTHS_RANGE_PRESET_LOCAL_FULL,
  LAST_7_DAYS_RANGE_PRESET_LOCAL_FULL,
  RangePreset,
} from 'cio/utils/date-picker';
import { buildMomentInLocalTime } from 'cio/utils/metrics/common';
import { TimeRange } from 'cio/utils/time';
import moment from 'moment-timezone';

interface CioDeliveriesDatePickerArgs {
  timeRange: TimeRange | null;
  onRangeSelect: (range: TimeRange | null) => void;
  disabled?: boolean;
  canClear?: boolean;
  maxRange?: string;
  dateRangePresets?: RangePreset[];
  dateRangeTooltipMessage?: string;
}

export default class CioDeliveriesDatePicker extends Component<CioDeliveriesDatePickerArgs> {
  maxDate: moment.Moment;

  constructor(owner: unknown, args: CioDeliveriesDatePickerArgs) {
    super(owner, args);
    // Using a new Date object to get the current date and time so that we are not constrained by UTC time
    // For timezones that are  + UTC, the max date was being set to the previous day when using moment.utc()
    // The new date object will be returned as a number. maxDate has been created as a moment object so we need to convert the number to a moment object
    // which is why we do moment(now)
    let now = new Date();
    this.maxDate = moment(now);
  }

  get disabled() {
    return this.args.disabled ?? false;
  }

  dateRangePresets = this.args.dateRangePresets ?? [
    LAST_7_DAYS_RANGE_PRESET_LOCAL_FULL,
    LAST_30_DAYS_RANGE_PRESET_LOCAL_FULL,
    LAST_12_WEEKS_FROM_NOW_RANGE_PRESET_LOCAL_FULL,
    LAST_6_MONTHS_RANGE_PRESET_LOCAL_FULL,
  ];

  get maxRange() {
    return this.args.maxRange ?? '1 year';
  }

  get timeRangeInLocalTimezone() {
    if (this.args.timeRange?.start == null || this.args.timeRange?.end == null) {
      return null;
    }

    return {
      start: buildMomentInLocalTime(this.args.timeRange.start).startOf('day'),
      end: buildMomentInLocalTime(this.args.timeRange.end).endOf('day'),
    };
  }

  get canClear() {
    return this.args.canClear ?? false;
  }

  @action
  onRangeSelect(value: { date: TimeRange | null }) {
    // if the whole date value has been nullified, user cannot possibly be selecting a range, they want a fresh start
    if (value.date == null) {
      this.args.onRangeSelect(null);
      return;
    }

    if (value.date.start == null || value.date.end == null) {
      return;
    }
    let { start, end } = value.date;
    let selectedPreset = this.dateRangePresets.find((preset) => {
      let presetRange = preset.getRange();
      if (presetRange.start == null || presetRange.end == null) {
        return false;
      }
      return (presetRange.start as moment.Moment).isSame(start) && (presetRange.end as moment.Moment).isSame(end);
    });

    // If the user selects only one day, therefore the timestamps are the same, we want to grab data for the whole day
    if (value.date.start.unix() === value.date.end.unix()) {
      let singleDay: TimeRange = {
        start: buildMomentInLocalTime(value.date.start).startOf('day'),
        end: buildMomentInLocalTime(value.date.end).endOf('day'),
      };
      return this.args.onRangeSelect(singleDay);
    }

    if (selectedPreset != null) {
      return this.args.onRangeSelect(selectedPreset.getRange());
    } else {
      // If the user selects a custom range with today as the end date, get until current timestamp
      if (value.date.end.format('YYYY-MM-DD') === this.maxDate.format('YYYY-MM-DD')) {
        let range: TimeRange = {
          start: buildMomentInLocalTime(value.date.start).startOf('day'),
          end: buildMomentInLocalTime(moment(new Date())),
        };
        return this.args.onRangeSelect(range);
      }
      let customRange: TimeRange = {
        start: buildMomentInLocalTime(value.date.start).startOf('day'),
        end: buildMomentInLocalTime(value.date.end).endOf('day'),
      };
      return this.args.onRangeSelect(customRange);
    }
  }
}
